import { useContext, useEffect, useState } from "react";
import { Button, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ToastContext } from "../../component/global/ToastContext";
import { aiBillingApi, coinPriceApi } from "../../features/ai-billing/api";
import { isSurveySendTariff } from "../surveys/sendBilling";
import "../../features/ai-billing/style.scss";

const tomanFromPrice = (price) => {
  if (!price) return "";
  if (price.toman_per_coin != null) return String(price.toman_per_coin);
  if (price.rial_per_coin != null) {
    return String(Math.floor(Number(price.rial_per_coin) / 10));
  }
  return "";
};

const CoinPriceSettings = () => {
  const { t } = useTranslation();
  const show = useContext(ToastContext);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState(null);
  const [toman, setToman] = useState("");

  const applyPrice = (price) => {
    setCurrent(price || null);
    setToman(tomanFromPrice(price));
  };

  const load = () => {
    setLoading(true);
    coinPriceApi
      .current()
      .then(applyPrice)
      .catch((error) => {
        if (error?.response?.status === 404) {
          applyPrice(null);
          return;
        }
        show(t("OrgManagement.Error"), "danger");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const save = () => {
    const value = Number(toman);
    if (!Number.isFinite(value) || value < 1 || !Number.isInteger(value)) {
      show(t("OrgManagement.Settings.CoinPrice.Invalid"), "danger");
      return;
    }
    const rialPerCoin = value * 10;
    if (current && Number(current.rial_per_coin) === rialPerCoin) {
      show(t("OrgManagement.Settings.CoinPrice.Unchanged"), "success");
      return;
    }
    setSaving(true);
    coinPriceApi
      .publish(rialPerCoin)
      .then((price) => {
        applyPrice(price);
        show(t("OrgManagement.Settings.CoinPrice.Success"), "success");
      })
      .catch(() => show(t("OrgManagement.Error"), "danger"))
      .finally(() => setSaving(false));
  };

  return (
    <section className="ai-usage-settings__price" aria-labelledby="org-coin-price-title">
      <div className="ai-usage-settings__price-head">
        <h2 id="org-coin-price-title" className="ai-usage-settings__price-title">
          {t("OrgManagement.Settings.CoinPrice.Title")}
        </h2>
        <p className="ai-usage-settings__lead">
          {t("OrgManagement.Settings.CoinPrice.Lead")}
        </p>
      </div>
      {loading ? (
        <div className="organizations_loading">
          <Spinner animation="border" size="sm" /> {t("OrgManagement.Loading")}
        </div>
      ) : (
        <div className="ai-usage-settings__price-row">
          <div className="ai-usage-settings__price-field">
            <label htmlFor="org-coin-price-input">
              {t("OrgManagement.Settings.CoinPrice.Label")}
            </label>
            <div className="ai-usage-settings__price-input-wrap">
              <input
                id="org-coin-price-input"
                className="ai-usage-coins-input"
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                value={toman}
                onChange={(e) => setToman(e.target.value)}
              />
              <span className="ai-usage-settings__price-unit">
                {t("OrgManagement.Settings.CoinPrice.Unit")}
              </span>
              <Button variant="primary" disabled={saving} onClick={save}>
                {saving ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  t("OrgManagement.Settings.CoinPrice.Save")
                )}
              </Button>
            </div>
            {current?.version ? (
              <p className="ai-usage-settings__price-meta">
                {t("OrgManagement.Settings.CoinPrice.Version", {
                  version: current.version,
                })}
              </p>
            ) : (
              <p className="ai-usage-settings__price-meta">
                {t("OrgManagement.Settings.CoinPrice.Empty")}
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

const asFormRow = (item) => ({
  ...item,
  coins: String(item.coins ?? 0),
  estimated_tokens: String(item.estimated_tokens ?? 0),
});

const AiUsageSettingsTab = () => {
  const { t } = useTranslation();
  const show = useContext(ToastContext);
  const [aiRows, setAiRows] = useState([]);
  const [sendRows, setSendRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingAi, setSavingAi] = useState(false);
  const [savingSend, setSavingSend] = useState(false);

  const load = () => {
    setLoading(true);
    aiBillingApi
      .listTariffs()
      .then((list) => {
        const mapped = list.map(asFormRow);
        setAiRows(mapped.filter((item) => !isSurveySendTariff(item.code)));
        setSendRows(mapped.filter((item) => isSurveySendTariff(item.code)));
      })
      .catch(() => show(t("OrgManagement.Error"), "danger"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const updateAiRow = (id, field, value) => {
    setAiRows((current) =>
      current.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const updateSendRow = (id, field, value) => {
    setSendRows((current) =>
      current.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const saveRows = (rows, { includeTokens, onSaving, successKey, apply }) => {
    const items = [];
    for (const row of rows) {
      const coins = Number(row.coins);
      if (!Number.isFinite(coins) || coins < 0) {
        show(t("OrgManagement.Settings.Coins.Invalid"), "danger");
        return;
      }
      const payload = { id: row.id, coins };
      if (includeTokens) {
        const estimatedTokens = Number(row.estimated_tokens);
        if (!Number.isFinite(estimatedTokens) || estimatedTokens < 1) {
          show(t("OrgManagement.Settings.Tokens.Invalid"), "danger");
          return;
        }
        payload.estimated_tokens = estimatedTokens;
      }
      items.push(payload);
    }
    onSaving(true);
    aiBillingApi
      .saveTariffs(items)
      .then((list) => {
        apply(list.map(asFormRow));
        show(t(successKey), "success");
      })
      .catch(() => show(t("OrgManagement.Error"), "danger"))
      .finally(() => onSaving(false));
  };

  const saveAi = () =>
    saveRows(aiRows, {
      includeTokens: true,
      onSaving: setSavingAi,
      successKey: "OrgManagement.Settings.Save.Success",
      apply: setAiRows,
    });

  const saveSend = () =>
    saveRows(sendRows, {
      includeTokens: false,
      onSaving: setSavingSend,
      successKey: "OrgManagement.Settings.Send.Save.Success",
      apply: setSendRows,
    });

  return (
    <div
      id="org-panel-settings"
      role="tabpanel"
      aria-labelledby="org-tab-settings"
      className="ai-usage-settings"
    >
      <CoinPriceSettings />
      <div className="organizations_toolbar">
        <div className="organizations_title">
          {t("OrgManagement.Settings.Title")}
        </div>
      </div>
      <p className="ai-usage-settings__lead">
        {t("OrgManagement.Settings.Lead")}
      </p>
      <div className="organizations_table_wrap">
        {loading ? (
          <div className="organizations_loading">
            <Spinner animation="border" size="sm" /> {t("OrgManagement.Loading")}
          </div>
        ) : aiRows.length === 0 ? (
          <div className="organizations_empty">
            {t("OrgManagement.Settings.Empty")}
          </div>
        ) : (
          <Table responsive className="organizations_table">
            <thead>
              <tr>
                <th>{t("OrgManagement.Settings.Operation")}</th>
                <th>{t("OrgManagement.Settings.Model")}</th>
                <th>{t("OrgManagement.Settings.Tokens")}</th>
                <th>{t("OrgManagement.Settings.Coins")}</th>
              </tr>
            </thead>
            <tbody>
              {aiRows.map((row) => (
                <tr key={row.id}>
                  <td className="org_name_cell">
                    {t(`ai.coins.op.${row.code}`, { defaultValue: row.label })}
                  </td>
                  <td>
                    <span className="ai-usage-settings__tier">
                      {t(`ai.coins.tier.${row.model_tier}`)}
                    </span>
                  </td>
                  <td>
                    <input
                      className="ai-usage-coins-input"
                      type="number"
                      min="1"
                      inputMode="numeric"
                      aria-label={t("OrgManagement.Settings.Tokens")}
                      value={row.estimated_tokens}
                      onChange={(e) =>
                        updateAiRow(row.id, "estimated_tokens", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="ai-usage-coins-input"
                      type="number"
                      min="0"
                      inputMode="numeric"
                      aria-label={t("OrgManagement.Settings.Coins")}
                      value={row.coins}
                      onChange={(e) =>
                        updateAiRow(row.id, "coins", e.target.value)
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
      <div className="ai-usage-settings__actions">
        <Button
          variant="primary"
          disabled={savingAi || loading || aiRows.length === 0}
          onClick={saveAi}
        >
          {savingAi ? (
            <Spinner animation="border" size="sm" />
          ) : (
            t("OrgManagement.Settings.Save")
          )}
        </Button>
      </div>

      <div className="organizations_toolbar">
        <div className="organizations_title">
          {t("OrgManagement.Settings.Send.Title")}
        </div>
      </div>
      <p className="ai-usage-settings__lead">
        {t("OrgManagement.Settings.Send.Lead")}
      </p>
      <div className="organizations_table_wrap">
        {loading ? (
          <div className="organizations_loading">
            <Spinner animation="border" size="sm" /> {t("OrgManagement.Loading")}
          </div>
        ) : sendRows.length === 0 ? (
          <div className="organizations_empty">
            {t("OrgManagement.Settings.Empty")}
          </div>
        ) : (
          <Table responsive className="organizations_table">
            <thead>
              <tr>
                <th>{t("OrgManagement.Settings.Operation")}</th>
                <th>{t("OrgManagement.Settings.Send.Unit")}</th>
                <th>{t("OrgManagement.Settings.Coins")}</th>
              </tr>
            </thead>
            <tbody>
              {sendRows.map((row) => (
                <tr key={row.id}>
                  <td className="org_name_cell">
                    {t(`ai.coins.op.${row.code}`, { defaultValue: row.label })}
                  </td>
                  <td>
                    {t(`OrgManagement.Settings.Send.Unit.${row.code}`)}
                  </td>
                  <td>
                    <input
                      className="ai-usage-coins-input"
                      type="number"
                      min="0"
                      inputMode="numeric"
                      aria-label={t("OrgManagement.Settings.Coins")}
                      value={row.coins}
                      onChange={(e) =>
                        updateSendRow(row.id, "coins", e.target.value)
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
      <div className="ai-usage-settings__actions">
        <Button
          variant="primary"
          disabled={savingSend || loading || sendRows.length === 0}
          onClick={saveSend}
        >
          {savingSend ? (
            <Spinner animation="border" size="sm" />
          ) : (
            t("OrgManagement.Settings.Send.Save")
          )}
        </Button>
      </div>
    </div>
  );
};

export default AiUsageSettingsTab;
